import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import { kpisAPI } from '../../services/api';
import FreshnessBadge from './FreshnessBadge';
import DrillModal from './DrillModal';
import { formatKpiValue, describeChange, PERIOD_LABEL, kpiDrillQuery } from '../../utils/kpiFormat';
import '../../styles/Scorecard.css';

const DEFAULT_REFRESH_MINUTES = 15;

/** Tiny inline sparkline with an optional target line. */
export const Sparkline = ({ trend = [], target = null }) => {
  const points = trend.filter((p) => p.value !== null && p.value !== undefined);
  if (points.length < 2) return <svg className="scorecard-spark" viewBox="0 0 100 28" aria-hidden="true" />;
  const values = points.map((p) => p.value);
  const all = target !== null && target !== undefined ? [...values, Number(target)] : values;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;
  const y = (v) => 24 - ((v - min) / span) * 20;
  const step = 100 / (points.length - 1);
  const path = points.map((p, i) => `${(i * step).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  return (
    <svg className="scorecard-spark" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden="true">
      {target !== null && target !== undefined && <line className="target" x1="0" x2="100" y1={y(Number(target))} y2={y(Number(target))} />}
      <polyline points={path} />
    </svg>
  );
};

export const ScorecardTile = ({ result, onOpen, showName = true }) => {
  const { kpi, value, target, status, trend, error, period } = result;
  const change = describeChange(result);
  return (
    <div
      className={`scorecard-tile status-${status || 'none'}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen && onOpen(kpi)}
      onKeyDown={(e) => { if (e.key === 'Enter' && onOpen) onOpen(kpi); }}
      title={`${kpi.name} — ${PERIOD_LABEL[kpi.cadence] || period?.label || ''}`}
    >
      {showName && <div className="scorecard-name">{kpi.name}</div>}
      <div className="scorecard-value">{formatKpiValue(value, kpi.format, kpi.unit)}</div>
      <div className="scorecard-meta">
        {error ? (
          <span className="scorecard-error" title={error}>⚠ unavailable</span>
        ) : change ? (
          <span className={`scorecard-change ${change.improving === null ? '' : change.improving ? 'improving' : 'worsening'}`}>
            {change.label}
          </span>
        ) : (
          <span className="scorecard-change">{kpi.timeDimension ? (PERIOD_LABEL[kpi.cadence] || '') : 'As of today'}</span>
        )}
        {target !== null && target !== undefined && (
          <span className="scorecard-target">Target {formatKpiValue(target, kpi.format, kpi.unit)}</span>
        )}
      </div>
      {trend?.filter((p) => p.value !== null && p.value !== undefined).length >= 2 && <Sparkline trend={trend} target={target} />}
    </div>
  );
};

/**
 * Dashboard widget (widget_kind = 'kpi'): one KPI, evaluated through the
 * reporting engine and shown as a RAG tile. Its title bar carries the KPI's
 * name, so it drags like any other widget, and the body is that KPI's card.
 * config: { tile: true, kpiIds: [id], kpiName, title?, refreshMinutes? }
 */
const ScorecardWidget = ({ widget }) => {
  const { config = {} } = widget;
  // One KPI: the first, should an older widget name more.
  const kpiIds = useMemo(() => (config.kpiIds || []).slice(0, 1), [config.kpiIds]);
  const refreshMinutes = config.refreshMinutes || DEFAULT_REFRESH_MINUTES;
  const currentCompany = useStore((state) => state.currentCompany);
  const companyId = currentCompany?.id;
  const navigate = useNavigate();
  const wallMode = useStore((s) => s.wallMode);
  const [drill, setDrill] = useState(false);

  const [state, setState] = useState({ loading: true, error: null, results: [], evaluatedAt: null });
  const inFlight = useRef(false);

  const load = async () => {
    if (!companyId || inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await kpisAPI.evaluate(companyId, kpiIds);
      if (!res?.success) throw new Error(res?.error || 'Evaluation failed');
      setState({ loading: false, error: null, results: res.data.results || [], evaluatedAt: res.data.evaluatedAt });
    } catch (err) {
      setState((prev) => ({ ...prev, loading: false, error: err.response?.data?.error || err.message }));
    } finally {
      inFlight.current = false;
    }
  };

  useEffect(() => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    load();
    const id = setInterval(load, Math.max(1, refreshMinutes) * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, kpiIds.join(','), refreshMinutes]);

  const single = state.results[0] || null;
  const title = config.title || single?.kpi.name || config.kpiName || 'KPI';
  const openKpi = (kpi) => navigate(`/kpis/${kpi.id}`);
  const drillQuery = single && !single.error ? kpiDrillQuery(single) : null;

  const renderBody = () => {
    if (state.loading && state.results.length === 0) return <div className="report-status">Loading…</div>;
    if (state.error) return <div className="report-status report-status--error">⚠ {state.error}</div>;
    if (!single) return <div className="scorecard-empty">This KPI has been deleted or deactivated.</div>;
    return <ScorecardTile result={single} onOpen={openKpi} showName={false} />;
  };

  return (
    <div className={`widget report-widget scorecard-widget scorecard-widget--tile status-${single?.status || 'none'}`}>
      <div className="widget-header">
        <span className="widget-title" title={title}>{title}</span>
        <span className="report-header-tools">
          {!wallMode && drillQuery && <button type="button" className="report-tool" title="Show the rows behind this" onClick={() => setDrill(true)}>Rows</button>}
          <FreshnessBadge kind="report" at={state.evaluatedAt} staleAfterMs={refreshMinutes * 3 * 60 * 1000} loading={state.loading} />
        </span>
      </div>
      <div className="widget-body report-body">{renderBody()}</div>
      {drill && drillQuery && <DrillModal companyId={companyId} row={{}} query={drillQuery} measure={single.kpi.measure} title={title} onClose={() => setDrill(false)} />}
    </div>
  );
};

export default ScorecardWidget;
